import React, { useMemo } from "react";
import { Alert, Box, Chip, Divider, Stack, Typography } from "@mui/material";
import type { ReviewEntity } from "../types";

interface IncidentSpecificsPanelProps {
  entities: ReviewEntity[];
}

const confidenceColor: Record<
  NonNullable<ReviewEntity["confidence"]>,
  "success" | "warning" | "error"
> = {
  high: "success",
  medium: "warning",
  low: "error",
};

// Groups by category while preserving the order the planner emitted them in,
// so the officer reads the specifics in the same sequence as the First Draft.
const groupByCategory = (entities: ReviewEntity[]): [string, ReviewEntity[]][] => {
  const groups = new Map<string, ReviewEntity[]>();
  for (const entity of entities) {
    const list = groups.get(entity.category);
    if (list) list.push(entity);
    else groups.set(entity.category, [entity]);
  }
  return [...groups.entries()];
};

export const IncidentSpecificsPanel: React.FC<IncidentSpecificsPanelProps> = ({
  entities,
}) => {
  const groups = useMemo(() => groupByCategory(entities), [entities]);

  if (groups.length === 0) {
    return (
      <Alert severity="info">
        The AI Assistant did not extract any incident specifics for this First Draft.
      </Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      {groups.map(([category, items], groupIndex) => (
        <Box key={category}>
          {groupIndex > 0 && <Divider sx={{ mb: 2.5 }} />}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="overline"
              sx={{ fontWeight: 700, letterSpacing: 1.2 }}
            >
              {category}
            </Typography>
            <Chip
              label={items.length}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.7rem" }}
            />
          </Stack>

          <Stack spacing={1}>
            {items.map((entity) => (
              <Box
                key={entity.id}
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, wordBreak: "break-word" }}
                >
                  {entity.label || "(untitled)"}
                </Typography>
                {entity.details && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.25 }}
                  >
                    {entity.details}
                  </Typography>
                )}
                {(entity.source || entity.confidence) && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 0.75 }}
                  >
                    {entity.source && (
                      <Chip
                        label={entity.source}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                    {entity.confidence && (
                      <Chip
                        label={`${entity.confidence} confidence`}
                        size="small"
                        color={confidenceColor[entity.confidence]}
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
